import { Box, Paper, Typography } from "@mui/material";

export interface HistogramProps {
  title: string;
  subTitle: string;
  icon?: React.ReactNode; // Optional icon to display next to title
  buckets: number[];
  labels: string[];
  colors: string[];
  textColors: string[];
  height?: number;
  showPercentages?: boolean; // Whether to show percentages instead of absolute values
  filterKey?: string; // Optional key to use for filtering when clicking a bar
  onFilterClick?: (key: string, value: string) => void; // New prop
  isLoading?: boolean; // Whether the component is in a loading state
}

const Histogram: React.FC<HistogramProps> = ({
  title,
  subTitle,
  icon,
  buckets,
  labels,
  colors,
  textColors,
  height = 250,
  showPercentages = false,
  filterKey,
  onFilterClick,
  isLoading = false
}) => {
  const handleBarClick = (index: number) => {
    // Only emit click if we have a filterKey and the label exists
    if (filterKey && labels[index] && onFilterClick) {
      onFilterClick(filterKey, labels[index].toLowerCase());
    }
  };

  return (
    <Paper sx={{ pt: 2, pb: 1, pl: 2, pr: 2, height: "100%" }}>
      {/* Title with icon, styled like MetricCard */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 4 }}>
        {icon && <Box sx={{ mr: 1 }}>{icon}</Box>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <Typography variant="subtitle1" pb={0} mb={0}>{title}</Typography>
          <Typography variant="body2" color="text.disabled" pt={0} mt={0} sx={{ lineHeight: 1, fontWeight: "medium" }}>{subTitle}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", height }}>
        {/* Create a custom histogram with div elements for better control */}
        <Box sx={{ display: "flex", flexGrow: 1, alignItems: "flex-end", mb: 0.5 }}>
          {isLoading ? (
            // Show loading indicator when isLoading is true
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
              <Box sx={{ display: "inline-block", position: "relative", width: 40, height: 40 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "4px solid rgba(25, 118, 210, 0.2)",
                    borderRadius: "50%",
                    borderTop: "4px solid rgba(25, 118, 210, 1)",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" }
                    }
                  }}
                />
              </Box>
            </Box>
          ) : buckets.some(count => count > 0) ? (
            // If we have data, show the histogram
            buckets.map((count, index) => {
              // Calculate height percentage based on max value
              const maxCount = Math.max(...buckets);
              const heightPercentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
              // Ensure that we show at least a small bar even for zero counts
              const minHeight = 5;
              // Calculate percentage for display
              const percentage = Math.round((count / Math.max(1, buckets.reduce((sum, val) => sum + val, 0))) * 100);

              return (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    width: `${100 / buckets.length}%`,
                    height: `${Math.max(heightPercentage, count > 0 ? minHeight : 0)}%`,
                    backgroundColor: colors[index],
                    mx: 0.5,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end", // Changed to flex-end to place value at bottom
                    borderRadius: "4px 4px 0 0",
                    transition: "all 0.3s ease",
                    cursor: filterKey ? "pointer" : "default",
                    "&:hover": {
                      opacity: 0.8,
                      transform: "scale(1.02)"
                    }
                  }}
                  onClick={() => count > 0 && handleBarClick(index)}
                >
                  {/* Only show percentage for bars that have a value */}
                  {count > 0 && showPercentages && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: "-24px", // Position above the bar
                        color: "text.secondary", // Mid-gray color
                        fontWeight: "medium",
                        textAlign: "center"
                      }}
                    >
                      {`${percentage}%`}
                    </Typography>
                  )}

                  {/* Show raw count inside the bar */}
                  {!showPercentages && (
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: textColors[index],
                        fontWeight: "bold"
                      }}
                    >
                      {count}
                    </Typography>
                  )}
                </Box>
              );
            })
          ) : (
            // If no data, show a message
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
              <Typography variant="body1" color="text.secondary">
                No data available
              </Typography>
            </Box>
          )}
        </Box>

        {/* X-axis labels - only show when we have data */}
        {buckets.some(count => count > 0) && (
          <Box sx={{ display: "flex", justifyContent: "space-around", width: "100%" }}>
            {labels.map((label, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  width: `${100 / buckets.length}%`,
                  textAlign: "center",
                  cursor: filterKey ? "pointer" : "default",
                  "&:hover": filterKey ? { textDecoration: "underline" } : {}
                }}
                onClick={() => buckets[index] > 0 && handleBarClick(index)}
              >
                {label}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default Histogram;
